// cart-drawer-recommendations.js
//
// Fetches recommended products via Shopify's Section Rendering API so that
// the theme's real quick-add snippet is rendered server-side.
//
// Also defines <rec-tabs> which handles:
//   • All / CO Brand / Season tab filtering
//   • Dot indicators synced to the scroll position
//   • Auto-scroll that cycles visible cards, pausing on hover or touch

/* ─────────────────────────────────────────────────────────────
   RecTabs
───────────────────────────────────────────────────────────── */

class RecTabs extends HTMLElement {

  /** @type {HTMLElement[]} — currently visible cards */
  #visibleCards = [];

  /** @type {number} — setInterval handle */
  #autoScrollTimer = null;

  /** @type {number} — index of the card currently snapped to */
  #currentIndex = 0;

  /** @type {boolean} — pause auto-scroll while user is interacting */
  #paused = false;

  /** @type {ResizeObserver} */
  #resizeObserver = null;

  connectedCallback() {
    this.#init();
  }

  disconnectedCallback() {
    this.#stopAutoScroll();
    this.#resizeObserver?.disconnect();
  }

  // ── Bootstrap ──────────────────────────────────────────────

  #hasOpenDropdown() {
  return !!this.querySelector('.cart-rec-variant-dropdown.is-open');
}

  #init() {
    // Re-clone buttons to strip any stale listeners from cache restores
    this.querySelectorAll('.rec-tabs__btn').forEach((btn) => {
      const fresh = btn.cloneNode(true);
      btn.replaceWith(fresh);
    });

    this.querySelectorAll('.rec-tabs__btn').forEach((btn) => {
      btn.addEventListener('click', () => this.#switchTab(btn.dataset.tab));
    });

    // Ensure dots container exists
    if (!this.querySelector('.rec-dots')) {
      const dots = document.createElement('div');
      dots.className = 'rec-dots';
      dots.setAttribute('aria-hidden', 'true');
      const wrapper = this.querySelector('.cart-drawer-recommendations__grid-wrapper');
      if (wrapper) wrapper.after(dots);
    }

    this.#updateVisibleCards();
    this.#buildDots();
    this.#bindScroll();
    this.#bindSwipe();
    this.#bindHoverPause();
    this.#bindItemHoverPause();
    this.#startAutoScroll();

    // Rebuild dots if the drawer resizes (e.g. viewport change)
    this.#resizeObserver = new ResizeObserver(() => this.#buildDots());
    const wrapper = this.querySelector('.cart-drawer-recommendations__grid-wrapper');
    if (wrapper) this.#resizeObserver.observe(wrapper);
  }

  // ── Tab switching ──────────────────────────────────────────

  #switchTab(activeTab) {
    this.querySelectorAll('.rec-tabs__btn').forEach((btn) => {
      const on = btn.dataset.tab === activeTab;
      btn.classList.toggle('rec-tabs__btn--active', on);
      btn.setAttribute('aria-selected', on ? 'true' : 'false');
    });

    this.querySelectorAll('[data-filters]').forEach((card) => {
      card.hidden = activeTab !== 'all' && !card.dataset.filters.split(' ').includes(activeTab);
    });

    // Reset state for the new set of visible cards
    this.#currentIndex = 0;
    this.#updateVisibleCards();
    this.#buildDots();
    this.#scrollToIndex(0, 'instant');
    this.#restartAutoScroll();
  }

  // ── Visible cards ──────────────────────────────────────────

  #updateVisibleCards() {
    this.#visibleCards = Array.from(
      this.querySelectorAll('.cart-drawer-recommendations__item:not([hidden])')
    );
  }

  // ── Dots ───────────────────────────────────────────────────

  #buildDots() {
    const dotsEl = this.querySelector('.rec-dots');
    if (!dotsEl) return;

    const count = this.#visibleCards.length;

    // Only show dots if more than one card
    if (count <= 1) {
      dotsEl.innerHTML = '';
      dotsEl.hidden = true;
      return;
    }

    dotsEl.hidden = false;
    dotsEl.innerHTML = Array.from({ length: count }, (_, i) => `
      <button
        class="rec-dot${i === this.#currentIndex ? ' rec-dot--active' : ''}"
        data-index="${i}"
        aria-label="Go to item ${i + 1}"
      ></button>
    `).join('');

    dotsEl.querySelectorAll('.rec-dot').forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.dataset.index, 10);
        this.#currentIndex = idx;
        this.#scrollToIndex(idx, 'smooth');
        this.#activateDot(idx);
        this.#restartAutoScroll();
      });
    });
  }

  #activateDot(index) {
    this.querySelectorAll('.rec-dot').forEach((d, i) => {
      d.classList.toggle('rec-dot--active', i === index);
    });
  }

  // ── Swipe (touch) ─────────────────────────────────────────
  // Native scroll is disabled (overflow:hidden) so we handle touch
  // entirely here. We intercept horizontal swipes, call preventDefault()
  // to kill vertical drift and momentum bleed, then snap to next/prev card.

  #bindSwipe() {
    const wrapper = this.querySelector('.cart-drawer-recommendations__grid-wrapper');
    if (!wrapper) return;

    let startX = 0;
    let startY = 0;
    let isHorizontal = null; // determined after first move

    wrapper.addEventListener('touchstart', (e) => {
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      isHorizontal = null;
      this.#paused = true;
    }, { passive: true });

    wrapper.addEventListener('touchmove', (e) => {
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;

      // Determine gesture direction on the first significant move
      if (isHorizontal === null && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
        isHorizontal = Math.abs(dx) >= Math.abs(dy);
      }

      // Block the event for horizontal gestures — prevents vertical drift
      // and stops the parent drawer from stealing the scroll
      if (isHorizontal) {
        e.preventDefault();
      }
    }, { passive: false }); // must be non-passive to call preventDefault

    wrapper.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      const threshold = 40; // px — minimum swipe distance to advance

      if (isHorizontal && Math.abs(dx) >= threshold) {
        if (dx < 0) {
          // Swipe left → next card
          const next = Math.min(this.#currentIndex + 1, this.#visibleCards.length - 1);
          this.#scrollToIndex(next);
        } else {
          // Swipe right → prev card
          const prev = Math.max(this.#currentIndex - 1, 0);
          this.#scrollToIndex(prev);
        }
      }

      setTimeout(() => {
        const hasOpenDropdown = this.querySelector('.cart-rec-variant-dropdown.is-open');
        if (!hasOpenDropdown) this.#paused = false;
      }, 2000);
    }, { passive: true });
  }

  // ── Scroll sync ────────────────────────────────────────────

  #bindScroll() {
    const wrapper = this.querySelector('.cart-drawer-recommendations__grid-wrapper');
    if (!wrapper) return;

    let scrollEndTimer;

    wrapper.addEventListener('scroll', () => {
      // Pause auto-scroll while the user is actively scrolling
      this.#paused = true;
      clearTimeout(scrollEndTimer);

      // Detect which card is most visible
      const wrapperLeft = wrapper.getBoundingClientRect().left;
      let closest = 0;
      let minDist = Infinity;

      this.#visibleCards.forEach((card, i) => {
        const dist = Math.abs(card.getBoundingClientRect().left - wrapperLeft);
        if (dist < minDist) { minDist = dist; closest = i; }
      });

      if (closest !== this.#currentIndex) {
        this.#currentIndex = closest;
        this.#activateDot(closest);
      }

      // Resume auto-scroll 2s after scrolling stops
      scrollEndTimer = setTimeout(() => {
        this.#paused = false;
      }, 2000);
    }, { passive: true });
  }

  // ── Hover / touch pause ────────────────────────────────────

  #bindHoverPause() {
    this.addEventListener('mouseenter', () => { this.#paused = true; });
    this.addEventListener('mouseleave', () => { this.#paused = false; });
    // Touch pause/resume is handled in #bindSwipe
  }

  // ── Individual item hover pause ────────────────────────────

  #bindItemHoverPause() {
    // Pause when hovering over any recommendation item
    this.addEventListener('mouseenter', (e) => {
      if (e.target.closest('.cart-drawer-recommendations__item')) {
        this.#paused = true;
      }
    }, true);

    this.addEventListener('mouseleave', (e) => {
      if (e.target.closest('.cart-drawer-recommendations__item')) {
        // Only unpause if no dropdown is open
        const hasOpenDropdown = this.querySelector('.cart-rec-variant-dropdown.is-open');
        if (!hasOpenDropdown) {
          this.#paused = false;
        }
      }
    }, true);
  }

  // ── Auto-scroll ────────────────────────────────────────────

  #startAutoScroll() {
    this.#stopAutoScroll();
    this.#autoScrollTimer = setInterval(() => {
      if (this.#paused || this.#hasOpenDropdown()) return;
      const count = this.#visibleCards.length;
      if (count <= 1) return;
      
      const nextIndex = (this.#currentIndex + 1) % count;
      
      // If we're going from last to first, scroll to the end first, then instantly jump to start
      if (this.#currentIndex === count - 1 && nextIndex === 0) {
        // Scroll to first card instantly (loop effect)
        this.#scrollToIndex(0, 'instant');
        this.#currentIndex = 0;
        this.#activateDot(0);
      } else {
        // Normal smooth scroll to next card
        this.#currentIndex = nextIndex;
        this.#scrollToIndex(nextIndex, 'smooth');
        this.#activateDot(nextIndex);
      }
    }, 3000);
  }

  #stopAutoScroll() {
    if (this.#autoScrollTimer) {
      clearInterval(this.#autoScrollTimer);
      this.#autoScrollTimer = null;
    }
  }

  #restartAutoScroll() {
    this.#stopAutoScroll();
    this.#startAutoScroll();
  }

  // ── Scroll helper ──────────────────────────────────────────

  #scrollToIndex(index, behavior = 'smooth') {
    const wrapper = this.querySelector('.cart-drawer-recommendations__grid-wrapper');
    const card = this.#visibleCards[index];
    if (!wrapper || !card) return;

    // getBoundingClientRect gives positions relative to the viewport — subtracting
    // wrapper's left and adding wrapper's current scrollLeft gives the correct
    // scrollLeft target regardless of offsetParent chain (which breaks on mobile).
    const wrapperRect = wrapper.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const targetScrollLeft = wrapper.scrollLeft + (cardRect.left - wrapperRect.left);

    wrapper.scrollTo({
      left: targetScrollLeft,
      behavior,
    });
  }

  // ── Public method to pause scrolling ───────────────────────

  pauseScroll() {
    this.#paused = true;
  }

  resumeScroll() {
    this.#paused = false;
  }
}

if (!customElements.get('rec-tabs')) {
  customElements.define('rec-tabs', RecTabs);
}

/* ─────────────────────────────────────────────────────────────
   CartDrawerRecommendations — fetches & caches the section HTML
───────────────────────────────────────────────────────────── */

class CartDrawerRecommendations extends HTMLElement {
  static #cache = new Map();

  #abortController = null;

  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 200px 0px' }
  );

  #onCartUpdate = () => {
    const productId = this.dataset.productId;
    if (!productId) return;

    requestAnimationFrame(() => {
      // Clear cache to force fresh load after cart update
      CartDrawerRecommendations.#cache.delete(productId);
      // Reload recommendations
      this.#loadRecommendations();
    });
  };

  connectedCallback() {
    const productId = this.dataset.productId;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    if (productId && CartDrawerRecommendations.#cache.has(productId)) {
      this.#restoreFromCache(productId);
    } else {
      this.#intersectionObserver.observe(this);
    }

    document.addEventListener('cart:update', this.#onCartUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #restoreFromCache(productId) {
    const cached = CartDrawerRecommendations.#cache.get(productId);
    if (!cached) return;
    const existing = this.querySelector('rec-tabs');
    if (!existing) return;
    // connectedCallback on the new <rec-tabs> re-inits everything
    existing.replaceWith(cached.cloneNode(true));
  }

  async #loadRecommendations() {
    const productId = this.dataset.productId;
    const limit     = this.dataset.limit  || 6;
    const intent    = this.dataset.intent || 'related';

    if (!productId) {
      console.warn('[cart-drawer-recommendations] No product ID provided');
      this.style.display = 'none';
      return;
    }

    try {
      const url =
        `/recommendations/products` +
        `?product_id=${productId}` +
        `&limit=${limit}` +
        `&intent=${intent}` +
        `&section_id=cart-drawer-rec-items`;

      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html   = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const rendered = parsed.querySelector('rec-tabs');

      if (
        !rendered ||
        rendered.querySelector('.cart-drawer-recommendations__grid')?.children.length === 0
      ) {
        this.style.display = 'none';
        return;
      }

      CartDrawerRecommendations.#cache.set(productId, rendered.cloneNode(true));

      const existing = this.querySelector('rec-tabs');
      if (existing) {
        existing.replaceWith(rendered);
      } else {
        this.appendChild(rendered);
      }
    } catch (error) {
      console.error('[cart-drawer-recommendations] Failed to load:', error);
      this.style.display = 'none';
    }
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}

/* ─────────────────────────────────────────────────────────────
   Cart Recommendations - Plus Icon & Variant Selection
───────────────────────────────────────────────────────────── */

(() => {
  /* ===============================
     TELEPORTED DROPDOWN HELPERS
     Moves the __list to <body> with position:fixed so it escapes
     the overflow-x:auto scroll container without being clipped.
  =============================== */

  let _teleportedList = null;    // the floating list currently in the dialog
  let _teleportSource = null;    // the original .cart-rec-variant-dropdown it came from
  let _teleportScrollEl = null;  // the grid-wrapper being watched for scroll
  let _teleportRoot = null;      // the <dialog> element we appended the list into

  function _positionTeleported(btn) {
    if (!_teleportedList) return;
    const rect = btn.getBoundingClientRect();

    // The teleported list is a direct child of the <dialog>, which is full-viewport.
    // Use position:absolute — coords from getBoundingClientRect() map 1:1 because
    // the dialog starts at 0,0. This also escapes overflow:hidden on inner containers.
    const listH = _teleportedList.offsetHeight;
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    let top, left;

    if (spaceAbove >= listH + 8 || spaceAbove > spaceBelow) {
      top = rect.top - listH - 8;
    } else {
      top = rect.bottom + 8;
    }

    const shiftLeft = 70; // adjust this value

    left = rect.left + rect.width / 2 - _teleportedList.offsetWidth / 2 - shiftLeft;
    // left = rect.left + rect.width / 2 - _teleportedList.offsetWidth / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - _teleportedList.offsetWidth - 8));

    _teleportedList.style.top  = top  + 'px';
    _teleportedList.style.left = left + 'px';
  }

  function _closeDropdown() {
    if (!_teleportedList) return;

    _teleportedList.classList.remove('is-visible');

    // Wait for fade-out transition then restore to original parent
    const list = _teleportedList;
    const source = _teleportSource;
    setTimeout(() => {
      if (list.parentNode && list.parentNode !== source) {
        source?.appendChild(list);
        list.classList.remove('is-teleported');
      }
    }, 220);

    if (_teleportScrollEl) {
      _teleportScrollEl.removeEventListener('scroll', _onWrapperScroll);
      _teleportScrollEl = null;
    }

    if (_teleportSource) {
      _teleportSource.classList.remove('is-open');
      const recTabs = _teleportSource.closest('rec-tabs');
      if (recTabs) recTabs.resumeScroll();
    }

    _teleportedList = null;
    _teleportSource = null;
    _teleportRoot = null;
  }

  function _onWrapperScroll() {
    _closeDropdown();
  }

  /* ===============================
     PLUS ICON → OPEN DROPDOWN
  =============================== */
  document.addEventListener('click', e => {
    const plus = e.target.closest('[data-open-variant]');
    if (!plus) return;

    e.preventDefault();
    e.stopPropagation();

    // Close any already-open dropdown first
    _closeDropdown();

    const item = plus.closest('.cart-drawer-recommendations__item');
    const dropdown = item?.querySelector('[data-variant-dropdown]');
    if (!dropdown) return;

    const list = dropdown.querySelector('.cart-rec-variant-dropdown__list');
    if (!list) return;

    // Pause auto-scroll
    const recTabs = plus.closest('rec-tabs');
    if (recTabs) recTabs.pauseScroll();

    dropdown.classList.add('is-open');

    // Teleport list into the cart drawer <dialog> (which is in the top layer via showModal).
    // Appending to <body> would put it BELOW the top layer and it would be invisible.
    const dialogRoot = plus.closest('cart-drawer-component')?.querySelector('dialog')
      ?? document.body;
    dialogRoot.appendChild(list);
    list.classList.add('is-teleported');

    _teleportedList = list;
    _teleportSource = dropdown;
    _teleportRoot = dialogRoot;

    // Position before showing (offsetHeight needs it in DOM)
    _positionTeleported(plus);

    // Animate in next frame
    requestAnimationFrame(() => list.classList.add('is-visible'));

    // Close on grid-wrapper scroll
    const wrapper = plus.closest('.cart-drawer-recommendations__grid-wrapper');
    if (wrapper) {
      _teleportScrollEl = wrapper;
      wrapper.addEventListener('scroll', _onWrapperScroll, { passive: true });
    }
  });

  /* ===============================
     CLICK OUTSIDE → CLOSE DROPDOWNS
  =============================== */
  document.addEventListener('click', e => {
    if (
      e.target.closest('.cart-rec-variant-dropdown__list') ||
      e.target.closest('[data-open-variant]')
    ) return;

    _closeDropdown();
  });

  /* ===============================
     SELECT SIZE → ADD TO CART
  =============================== */
  document.addEventListener('click', async e => {
    const item = e.target.closest('.cart-rec-variant-dropdown__item');
    if (!item) return;

    e.preventDefault();
    e.stopPropagation(); // Prevent any parent handlers

    // List is teleported (detached from original DOM), so fall back to _teleportSource
    const dropdown = item.closest('.cart-rec-variant-dropdown') || _teleportSource;
    if (!dropdown) return;

    const card = dropdown.closest('.cart-drawer-recommendations__item');
    const variantMain = dropdown.closest('.cart-rec-variant-main');

    if (!card || !variantMain) return;

    const variants = JSON.parse(variantMain.dataset.variants);
    const value = item.dataset.value;

    const variant = variants.find(v =>
  v.available &&
  v.options.includes(value)
);

    if (!variant) {
      console.warn('[cart-rec] No matching variant for', value);
      return;
    }

    // Close and retract teleported dropdown
    _closeDropdown();

    // Get cart sections
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sections = [];
    cartItemsComponents.forEach(el => {
      if (el.dataset.sectionId) sections.push(el.dataset.sectionId);
    });

    const formData = new FormData();
    formData.append('id', variant.id);
    formData.append('quantity', 1);
    sections.forEach(section => formData.append('sections', section));

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      }).then(r => r.json());

      // Dispatch cart update event
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          cart: response,
          data: {
            source: 'cart-recommendations',
            sections: response.sections || {}
          }
        }
      }));

    } catch (err) {
      console.error('[cart-rec] Add to cart failed', err);
    }
  });

  /* ===============================
     SINGLE VARIANT → ADD TO CART
  =============================== */
  document.addEventListener('click', async e => {
    const btn = e.target.closest('[data-add-single]');
    if (!btn || !btn.closest('.cart-drawer-recommendations__item')) return;

    e.preventDefault();
    e.stopPropagation(); // Prevent any parent handlers
    if (btn.disabled) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.disabled = true;
    const originalHTML = btn.innerHTML;

    // Show loading state
    btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20"><circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="2" fill="none" stroke-dasharray="50" stroke-dashoffset="25"><animateTransform attributeName="transform" type="rotate" from="0 10 10" to="360 10 10" dur="1s" repeatCount="indefinite"/></circle></svg>';

    // Get cart sections
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sections = [];
    cartItemsComponents.forEach(el => {
      if (el.dataset.sectionId) sections.push(el.dataset.sectionId);
    });

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    sections.forEach(section => formData.append('sections', section));

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      }).then(r => r.json());

      // Show success state
      btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 10l4 4 8-8"/></svg>';

      // Dispatch cart update event
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          cart: response,
          data: {
            source: 'cart-recommendations',
            sections: response.sections || {}
          }
        }
      }));

      // Reset button after delay
      setTimeout(() => {
        btn.innerHTML = originalHTML;
        btn.disabled = false;
      }, 1500);
    } catch (err) {
      console.error('[cart-rec] Single variant add failed', err);
      btn.innerHTML = originalHTML;
      btn.disabled = false;
    }
  });
  
})();